import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authApi } from '../services/api';
import axios from 'axios';

const AuthContext = createContext(null);

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const ROLE_ALIASES = {
    store_admin: 'admin',
    storeadmin: 'admin',
    store_owner: 'admin',
    owner: 'admin',
    administrator: 'admin',
    sales_staff: 'staff',
    sales: 'staff',
};

function normalizeRole(role) {
    const raw = String(role || 'staff').trim().toLowerCase().replace(/[\s-]+/g, '_');
    return ROLE_ALIASES[raw] || raw;
}

function normalizeStoredUser(rawUser, role = 'store') {
    if (!rawUser) return rawUser;
    const nextRole = role === 'store' && normalizeRole(rawUser.role) === 'super_admin'
        ? 'admin'
        : normalizeRole(rawUser.role);
    return { ...rawUser, role: nextRole, deploymentRole: role };
}

export function AuthProvider({ children }) {
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [deploymentRole, setDeploymentRole] = useState('store');
    const [user, setUser] = useState(() => {
        const raw = localStorage.getItem('user');
        if (!raw) return null;
        const savedRole = String(localStorage.getItem('deploymentRole') || 'store').toLowerCase() === 'master' ? 'master' : 'store';
        return normalizeStoredUser(JSON.parse(raw), savedRole);
    });
    const heartbeatRef = useRef(null);

    useEffect(() => {
        const checkDeploymentRole = async () => {
            try {
                const baseUrl = process.env.REACT_APP_API_URL || '/api';
                const { data } = await axios.get(`${baseUrl}/install/health`, {
                    timeout: 10000,
                    validateStatus: () => true,
                });
                const nextRole = String(data?.deploymentRole || 'store').toLowerCase() === 'master' ? 'master' : 'store';
                localStorage.setItem('deploymentRole', nextRole);
                setDeploymentRole(nextRole);

                const correctedUser = normalizeStoredUser(user, nextRole);
                if (correctedUser && JSON.stringify(correctedUser) !== JSON.stringify(user)) {
                    localStorage.setItem('user', JSON.stringify(correctedUser));
                    setUser(correctedUser);
                }
            } catch {
                localStorage.setItem('deploymentRole', 'store');
                setDeploymentRole('store');
            }
        };

        checkDeploymentRole();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Periodic heartbeat: verify store is still active while user is logged in.
    // Super-admins are exempt (they are the master — they can never be locked out this way).
    useEffect(() => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }

        if (!token || !user || user.role === 'super_admin') return;

        const check = async () => {
            try {
                const storedToken = localStorage.getItem('token');
                if (!storedToken) return;
                await axios.get(
                    `${process.env.REACT_APP_API_URL || '/api'}/tenants/me`,
                    { headers: { Authorization: `Bearer ${storedToken}` }, timeout: 10000 }
                );
            } catch {
                // The response interceptor in api.js handles STORE_DISABLED
            }
        };

        heartbeatRef.current = setInterval(check, HEARTBEAT_INTERVAL_MS);
        return () => {
            if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, user?.role]);

    const login = async (email, password) => {
        const res = await authApi.login({ email, password });
        if (res.success) {
            const nextDeploymentRole = String(res.data.deploymentRole || res.data.user?.deploymentRole || deploymentRole || 'store').toLowerCase() === 'master'
                ? 'master'
                : 'store';
            const nextUser = normalizeStoredUser(res.data.user, nextDeploymentRole);
            localStorage.setItem('token', res.data.token);
            localStorage.setItem('user', JSON.stringify(nextUser));
            localStorage.setItem('deploymentRole', nextDeploymentRole);
            setToken(res.data.token);
            setUser(nextUser);
            setDeploymentRole(nextDeploymentRole);
        }
        return res;
    };

    const register = async (payload) => {
        const res = await authApi.register(payload);
        if (res.success) {
            localStorage.setItem('token', res.data.token);
            localStorage.setItem('user', JSON.stringify(res.data.user));
            setToken(res.data.token);
            setUser(res.data.user);
        }
        return res;
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
    };

    const updateUser = (nextUser) => {
        const normalizedUser = normalizeStoredUser(nextUser, deploymentRole);
        setUser(normalizedUser);
        if (normalizedUser) {
            localStorage.setItem('user', JSON.stringify(normalizedUser));
        } else {
            localStorage.removeItem('user');
        }
    };

    const value = { token, user, deploymentRole, isMasterDeployment: deploymentRole === 'master', login, register, logout, updateUser };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    return useContext(AuthContext);
}
